/**
 * Validation utilities for form inputs
 */

/**
 * Validates if a string is a valid email address
 * SECURITY: Enhanced RFC 5322 compliant email validation
 *
 * This validator implements comprehensive email validation including:
 * - RFC 5322 compliant regex pattern
 * - Length validation (RFC 5321: max 254 chars total, max 64 chars local part)
 * - Domain validation (must have at least one dot)
 * - Protection against common malformed emails (e.g., "test@.", "test..@test.com")
 *
 * @param email - The email string to validate
 * @returns Object with isValid boolean and optional error message
 */
export const validateEmail = (email: string): { isValid: boolean; message?: string } => {
  // Check if email is provided
  if (!email) {
    return { isValid: false, message: 'Email is required' };
  }

  // Trim whitespace
  const trimmedEmail = email.trim();

  // Length check (RFC 5321: email addresses must be <= 254 characters)
  if (trimmedEmail.length > 254) {
    return { isValid: false, message: 'Email address is too long' };
  }

  // RFC 5322 compliant email regex
  // Allows: letters, numbers, and special chars (!#$%&'*+/=?^_`{|}~-) in local part
  // Requires: @ symbol, valid domain with proper format
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Basic format check
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }

  // Split email into local part and domain
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return { isValid: false, message: 'Email address is invalid' };
  }

  const [localPart, domain] = parts;

  // Local part must be <= 64 characters (RFC 5321)
  if (localPart.length > 64) {
    return { isValid: false, message: 'Email address is invalid' };
  }

  // Domain must have at least one dot (e.g., example.com)
  if (!domain.includes('.')) {
    return { isValid: false, message: 'Email domain is invalid' };
  }

  // Check for consecutive dots in local part (invalid per RFC)
  if (localPart.includes('..')) {
    return { isValid: false, message: 'Email address contains invalid consecutive dots' };
  }

  // Check for leading/trailing dots in local part
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return { isValid: false, message: 'Email address cannot start or end with a dot' };
  }

  return { isValid: true };
};

/**
 * Legacy email validation function (for backward compatibility)
 * DEPRECATED: Use validateEmail() instead for better security
 * @param email - The email string to validate
 * @returns true if valid email, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const result = validateEmail(email);
  return result.isValid;
};

/**
 * Validates if a string is not empty after trimming
 * @param value - The string to validate
 * @returns true if not empty, false otherwise
 */
export const isNotEmpty = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Validates password strength with comprehensive complexity requirements
 * @param password - The password to validate
 * @returns Object with isValid boolean and optional message
 */
export const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  // Minimum length check
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long'
    };
  }

  // Complexity requirements
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (!hasUpperCase) {
    return {
      isValid: false,
      message: 'Password must contain at least one uppercase letter'
    };
  }

  if (!hasLowerCase) {
    return {
      isValid: false,
      message: 'Password must contain at least one lowercase letter'
    };
  }

  if (!hasNumber) {
    return {
      isValid: false,
      message: 'Password must contain at least one number'
    };
  }

  if (!hasSpecial) {
    return {
      isValid: false,
      message: 'Password must contain at least one special character (!@#$%^&*...)'
    };
  }

  // Check against common passwords blacklist
  const commonPasswords = [
    'password', 'Password1!', 'Password123!', '12345678',
    'Qwerty123!', 'Welcome123!', 'Admin123!', 'User1234!',
    'passw0rd', 'Passw0rd!', 'Test1234!', 'Admin@123'
  ];

  if (commonPasswords.includes(password)) {
    return {
      isValid: false,
      message: 'This password is too common. Please choose a different password.'
    };
  }

  return { isValid: true };
};

/**
 * Validates if two passwords match
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns true if passwords match, false otherwise
 */
export const passwordsMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword;
};

/**
 * Validates username
 * @param username - The username to validate
 * @returns Object with isValid boolean and optional message
 */
export const validateUsername = (username: string): { isValid: boolean; message?: string } => {
  if (!isNotEmpty(username)) {
    return { isValid: false, message: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, message: 'Username must be at least 3 characters long' };
  }
  
  // Only allow alphanumeric, underscore, and hyphen
  const usernameRegex = /^[a-zA-Z0-9_-]+$/;
  if (!usernameRegex.test(username)) {
    return { isValid: false, message: 'Username can only contain letters, numbers, underscores, and hyphens' };
  }
  
  return { isValid: true };
};